<template>
  <v-card class="ma-6" color="#B8C8EE" elevation="0" v-if="!minimal">
    <template #prepend>
      <v-avatar
        v-if="!isLoading"
        :image="user?.[0]?.avatar ?? ''"
        color="#336AE9"
        icon="mdi-account-circle"
        size="48"
      />
      <v-skeleton-loader v-else color="transparent" type="avatar" />
    </template>
    <template #title>
      <span v-if="!isLoading" class="font-bold text-[16px]">{{
        userStore?.authUserData?.displayName ?? userStore?.authUserData?.email?.split('@')[0]
      }}</span>
      <v-skeleton-loader v-else color="transparent" type="list-item-two-line" />
    </template>
    <template #subtitle>
      <div v-if="!isLoading" class="grid">
        <span class="text-[#808080] text-[11px]">{{ organization?.name?.trade }}</span>
        <span class="text-[#336AE9] text-[11px] cursor-pointer font-bold">
          {{ t('$vuetify.more') ?? 'Mais' }}
          <v-menu activator="parent">
            <v-list class="rounded-xl">
              <v-list-item @click="router.push('/organizations')">
                <v-icon class="text-[#336AE9]">mdi-rotate-3d-variant</v-icon> Trocar instituição
              </v-list-item>
              <v-list-item @click="redirectToProfile">
                <v-icon class="text-[#336AE9]">mdi-account-edit-outline</v-icon> Editar perfil
              </v-list-item>
              <v-list-item @click="openZendesk">
                <v-icon class="text-[#336AE9]">mdi-help-circle-outline</v-icon> Preciso de ajuda
              </v-list-item>
              <v-list-item @click="openPrivacyPolicy">
                <v-icon class="text-[#336AE9]">mdi-security</v-icon> Política de privacidade
              </v-list-item>
              <v-list-item @click="logoutAction">
                <v-icon class="text-red">mdi-logout</v-icon> {{ t('$vuetify.logout') }}
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
      </div>
    </template>
  </v-card>
  <template v-else>
    <v-menu>
      <template #activator="{ props }">
        <v-avatar
          v-bind="props"
          :image="user?.[0]?.avatar ?? ''"
          color="#336AE9"
          icon="mdi-account-circle"
          size="40"
          class="cursor-pointer"
        />
      </template>
      <v-list class="rounded-xl">
        <v-list-item @click="router.push('/organizations')">
          <v-icon class="text-[#336AE9]">mdi-rotate-3d-variant</v-icon> Trocar instituição
        </v-list-item>
        <v-list-item @click="router.push('/organization-profile')">
          <v-icon class="text-[#336AE9]">mdi-account-edit-outline</v-icon> Editar perfil
        </v-list-item>
        <v-list-item @click="openZendesk">
          <v-icon class="text-[#336AE9]">mdi-help-circle-outline</v-icon> Preciso de ajuda
        </v-list-item>
        <v-list-item @click="openPrivacyPolicy">
          <v-icon class="text-[#336AE9]">mdi-security</v-icon> Política de privacidade
        </v-list-item>
        <v-list-item @click="logoutAction">
          <v-icon class="text-red">mdi-logout</v-icon> {{ t('$vuetify.logout') }}
        </v-list-item>
      </v-list>
    </v-menu>
  </template>
</template>

<script lang="ts" setup>
import { db } from '@/config/firebase'
import type { Organization } from '@/models/organization.model'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useLocale } from 'vuetify'
import { logout } from '@/services/auth.service'
import { collection, limit, query, where } from 'firebase/firestore'
import { useCollection } from 'vuefire'
import { useLoadingStore } from '@/stores/loading'
import { useFlutter } from '@/composables/useFlutter'
import UserService from '@/services/user.service'
import { useUserStore } from '@/stores/user'
import Level from '@/enums/level.enum'
import Environment from '@/config/environment'

defineProps({
  showEditProfile: {
    type: Boolean,
    default: false
  },
  minimal: {
    type: Boolean,
    default: false
  }
})

const userService = new UserService()

const router = useRouter()
const userStore = useUserStore()
const { t } = useLocale()
const loadingStore = useLoadingStore()
const flutter = useFlutter()

const redirectToProfile = async () => {
  try {
    const levels = await userService.getUserLevels()
    const isStudent = levels.some((l: any) => l.level === Level.STUDENT)
    const isOrganizationalRole = levels.some((l: any) => Number(l.level) >= 10)

    if (isStudent && !isOrganizationalRole) {
      router.push('/student/profile')
    } else {
      router.push('/organization-profile')
    }
  } catch (error) {
    router.push('/organization-profile')
  }
}

const userSource = computed(() =>
  query(collection(db, 'users'), where('email', '==', userStore.authUserData?.email), limit(1))
)
const user = useCollection(userSource)

const organization = ref({} as Organization)
const urls = ref({
  support: 'https://aprova.app/',
  privacyPolicy: 'https://expandel.com.br/politica-de-privacidade/',
  loginUrl: Environment.LOGIN_URL
})
const userLevel = ref(false)
const isLoading = ref(false)

onMounted(async () => {
  try {
    isLoading.value = true
    await userIsSuperAdmin()
  } finally {
    isLoading.value = false
  }
})

const logoutAction = async () => {
  try {
    loadingStore.startLoading()
    await logout(urls.value.loginUrl)
  } finally {
    loadingStore.stopLoading()
  }
}
const openZendesk = () => openExternalLink(urls.value.support)

const openPrivacyPolicy = () => openExternalLink(urls.value.privacyPolicy)

const openExternalLink = (url: string) =>
  flutter.isNativePlatform.value ? flutter.launchUrl(url) : window.open(url, '_blank')

const userIsSuperAdmin = async () => {
  const userLevels = await userService.getSingleUserById()
  if (userLevels?.is_super_admin) {
    userLevel.value = true
  }
}
</script>
